'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/shared/utils/tw';
import type { Toast } from '@/layouts/custom-avatar-page/toast/types/toast-types';

interface KioskToastProps {
  toast: Toast;
}

/**
 * 키오스크 전용 토스트 컴포넌트
 *
 * Figma 디자인 (node 2011:370 Bubble) 스펙 적용:
 * - 배경: rgba(255, 255, 255, 0.9)
 * - 테두리: rgba(255, 255, 255, 0.05), ~10.7px
 * - Border Radius: ~107px
 * - 패딩: 64px 96px (세로/가로)
 * - Backdrop Blur: ~107px
 * - 텍스트: Pretendard 500, 90px, #121314
 */
export const KioskToast = ({ toast }: KioskToastProps) => {
  // TTS 타입인 경우 타이핑 효과 적용
  const isTTS = toast.type === 'tts';
  const [displayedText, setDisplayedText] = useState(isTTS ? '' : toast.message);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // 텍스트 overflow 감지 (그라데이션 페이드아웃 조건)
  // 한번 true가 되면 false로 돌아가지 않음 (TTS 타이핑 중 깜빡임 방지)
  const checkOverflow = useCallback(() => {
    if (containerRef.current && !isOverflowing) {
      const overflows = containerRef.current.scrollHeight > containerRef.current.clientHeight;
      if (overflows) setIsOverflowing(true);
    }
  }, [isOverflowing]);

  // TTS 타이핑 효과
  useEffect(() => {
    if (!isTTS) {
      setDisplayedText(toast.message);
      return;
    }

    // 새 메시지가 이전 표시 텍스트보다 짧으면 리셋 (새 턴)
    if (toast.message.length < displayedText.length) {
      setDisplayedText('');
    }

    // 새로운 글자가 추가되었을 때만 타이핑 효과
    if (toast.message.length > displayedText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(toast.message.slice(0, displayedText.length + 1));
      }, 30); // 30ms마다 1글자 추가

      return () => clearTimeout(timer);
    }
  }, [toast.message, displayedText, isTTS]);

  // 텍스트 변경 시 overflow 체크 + 자동 스크롤 (최신 텍스트가 보이도록)
  useEffect(() => {
    checkOverflow();
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [displayedText, checkOverflow]);

  return (
    <div
      className={cn(
        'relative flex flex-col',
        // Figma: Glass Effect (fill: rgba(255,255,255,0.5))
        'bg-white/50 bg-clip-padding',
        // Figma: border-radius 80px in 2160w → 3.70cqw
        'rounded-[3.70cqw]',
        // Figma: padding 48px 80px → 80/2160=3.70cqw, 48/3840=1.25cqh
        'px-[3.70cqw] py-[1.25cqh]',
        // Max width & height 제한 (560px/3840 = 14.58cqh)
        'max-w-full',
        'max-h-[14.58cqh]',
        'overflow-hidden',
        // Animation
        toast.isExiting ? 'animate-toast-exit' : 'animate-toast-enter',
        // Pointer events
        'pointer-events-auto'
      )}
      style={{
        // Inner border: #fff 15% (box-shadow inset)
        boxShadow: 'inset 0 0 0 0.37cqw rgba(255,255,255,0.15)',
      }}
    >
      {/* 내부 스크롤 영역: 토스트 외곽(border-radius, box-shadow)은 유지하면서 텍스트만 스크롤+마스크 */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          // 스크롤바 숨김
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          // overflow 시 위쪽 텍스트만 페이드아웃 (컨텐츠 영역 내부)
          ...(isOverflowing ? {
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 100%)',
          } : {}),
        }}
      >
        <p
          className={cn(
            'font-pretendard font-medium text-left break-by-lang [overflow-wrap:break-word]',
            // Figma: 80px in 2160 width → 3.70cqw
            'text-[3.70cqw]',
            'leading-[1.5]',
            // Figma: letterSpacing -1%
            'tracking-[-0.01em]',
            // Figma: #121314
            'text-[#121314]'
          )}
        >
          {displayedText}
          {/* TTS 타이핑 중일 때 커서 표시 */}
          {isTTS && displayedText.length < toast.message.length && (
            <span className="inline-block w-1 h-[1em] ml-0.5 bg-[#121314]/60 animate-pulse" />
          )}
        </p>
      </div>
    </div>
  );
};

export default KioskToast;
